import type { CardBlock, CardContent, CardOverrides, CardPlan, LocalizedText } from './types'
import { OVERRIDABLE_FIELDS } from './types'

/** A translation in `language`, else English; per string, so a card may mix the two. */
export const resolveText = (text: LocalizedText | undefined | null, language: string): string => {
  if (!text) return ''
  const lang = language.split('-')[0] as keyof LocalizedText
  return text[lang]?.trim() || text.en || ''
}

export const formatPrice = (plan: Pick<CardPlan, 'priceAmount' | 'currency'>, language: string): string => {
  try {
    return new Intl.NumberFormat(language, { style: 'currency', currency: plan.currency.toUpperCase() }).format(plan.priceAmount / 100)
  } catch {
    return `${(plan.priceAmount / 100).toFixed(2)} ${plan.currency.toUpperCase()}`
  }
}

/**
 * Replaces `{{planName}}`, `{{price}}`, `{{interval}}`, `{{period}}`, `{{minutes}}` and
 * `{{modules}}` with the plan's values. `intervalWord` is the interval noun in the
 * reader's language ("month"); `periodPhrase` is "every month" or "for one month".
 * An unknown token is left as written; the server refuses those on save.
 */
export const fillTokens = (text: string, plan: CardPlan, language: string, intervalWord: string, periodPhrase = `every ${intervalWord}`): string => {
  const values: Record<string, string> = {
    planName: plan.name,
    price: formatPrice(plan, language),
    interval: intervalWord,
    period: periodPhrase,
    minutes: String(Math.round(plan.includedSeconds / 60)),
    modules: plan.moduleNames.join(', '),
  }
  return text.replace(/\{\{\s*([^}]*?)\s*\}\}/g, (whole, name: string) => values[name] ?? whole)
}

/**
 * The template with a plan's overrides applied, as the server does it for the offer.
 * Used by the admin previews to show unsaved overrides.
 */
export const mergeOverrides = (content: CardContent, overrides: CardOverrides | null | undefined): CardContent => {
  if (!overrides) return content
  return {
    ...content,
    badge: overrides.badge !== undefined ? overrides.badge : content.badge,
    blocks: content.blocks.map((block) => {
      const o = overrides.blocks?.[block.id]
      if (!o) return block
      const allowed = OVERRIDABLE_FIELDS[block.type] ?? []
      const picked = Object.fromEntries(Object.entries(o).filter(([field, v]) => allowed.includes(field as keyof typeof o) && v != null))
      return { ...block, ...picked } as CardBlock
    }),
  }
}

/** Splits `**bold**` runs out of a string; everything else stays plain text. */
export const splitBold = (text: string): { text: string; bold: boolean }[] =>
  text
    .split(/(\*\*[^*]+\*\*)/g)
    .filter(Boolean)
    .map((part) => (part.startsWith('**') && part.endsWith('**') && part.length > 4 ? { text: part.slice(2, -2), bold: true } : { text: part, bold: false }))
